import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Project } from "../types";
import { RootState } from "../store";
import { 
  useGetProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
} from "../store/api/projects";
import { useGetWorkspaceMembersQuery } from "../store/api/workspaces";
import { useGetTemplatesQuery } from "../store/api/templates";
import { useGetProjectTasksQuery, useApplyTemplateToProjectMutation } from "../store/api/tasks";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import * as yup from "yup";
import { Plus, Edit, Archive, ArchiveRestore, Users, ListTodo, UserPlus, FolderKanban, Trash2, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const { t, language } = useLanguage();

  // Get current workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || '';

  // Fetch projects from API
  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjectsQuery(
    workspaceId,
    { skip: !workspaceId }
  );

  // Fetch users from API
  const { data: users = [], isLoading: isLoadingUsers } = useGetWorkspaceMembersQuery(
    workspaceId,
    { skip: !workspaceId }
  );

  // Fetch templates from API
  const { data: taskTemplates = [], isLoading: isLoadingTemplates } = useGetTemplatesQuery(
    { workspaceId },
    { skip: !workspaceId }
  );

  // Mutations
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [archiveProject] = useArchiveProjectMutation();
  const [unarchiveProject] = useUnarchiveProjectMutation();
  const [applyTemplate] = useApplyTemplateToProjectMutation();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  
  // Confirmation modals state
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);
  const [archiveProjectId, setArchiveProjectId] = useState<string | null>(null);
  const [unarchiveProjectId, setUnarchiveProjectId] = useState<string | null>(null);
  
  // Loading states for mutations
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  // アクティブとアーカイブでプロジェクトをフィルタリング
  const activeProjects = projects.filter(p => !p.isArchived);
  const archivedProjects = projects.filter(p => p.isArchived);

  // Component to display task counts for a project
  const ProjectTaskCounts = ({ projectId }: { projectId: string }) => {
    const { data: tasks = [], isLoading } = useGetProjectTasksQuery(
      { workspaceId, projectId },
      { skip: !workspaceId || !projectId }
    );

    if (isLoading) {
      return (
        <>
          <TableCell className="text-center">
            <Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" />
          </TableCell>
          <TableCell className="text-center">
            <Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" />
          </TableCell>
        </>
      );
    }

    // Recurring tasks: tasks that are repeatable
    const recurringTasks = tasks.filter(t => t.isRepeatable).length;
    // One-time tasks: tasks that are not repeatable
    const oneTimeTasks = tasks.filter(t => !t.isRepeatable).length;

    return (
      <>
        <TableCell className="text-center">
          <span className="text-gray-900">{recurringTasks}</span>
        </TableCell>
        <TableCell className="text-center">
          <span className="text-gray-900">{oneTimeTasks}</span>
        </TableCell>
      </>
    );
  };

  // 新規プロジェクト作成用フォーム
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    memberIds: [] as string[],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reset form when modal state changes
  useEffect(() => {
    const isModalOpen = isCreateModalOpen || editingProject !== null;
    if (!isModalOpen) {
      // Modal is closed, reset everything
      setFormData({
        name: "",
        description: "",
        memberIds: [],
      });
      setErrors({});
      setSelectedTemplateId("");
      setShowTemplateSelect(false);
    }
  }, [isCreateModalOpen, editingProject]);

  // Yup validation schema
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('projects.validation.nameRequired'))
      .max(50, t('projects.validation.nameMaxLength')),
    description: yup
      .string()
      .max(150, t('projects.validation.descriptionMaxLength')),
    memberIds: yup
      .array()
      .of(yup.string())
      .min(1, t('projects.validation.membersRequired')),
  });

  const validateForm = async (): Promise<boolean> => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleCreate = async () => {
    // Validate form
    const isValid = await validateForm();
    if (!isValid || !workspaceId) return;
    
    try {
      const result = await createProject({
        workspaceId,
        data: {
      name: formData.name,
      description: formData.description,
      memberIds: formData.memberIds,
        },
      }).unwrap();

    // テンプレートが選択されていれば、タスクをインポート
      if (selectedTemplateId && result.id) {
        try {
          await applyTemplate({
            workspaceId,
            projectId: result.id,
            data: {
              templateId: selectedTemplateId,
              assigneeIds: formData.memberIds,
            },
          }).unwrap();
          toast.success(t('projects.templateApplied') || 'Template applied successfully');
        } catch (error) {
          console.error('Failed to apply template:', error);
          handleApiError(error, t('projects.templateApplyFailed') || 'Failed to apply template');
      }
    }

    setSelectedTemplateId("");
    setShowTemplateSelect(false);
    setIsCreateModalOpen(false);
      toast.success(t('projects.projectCreated') || 'Project created successfully');
    } catch (error) {
      console.error('Failed to create project:', error);
      handleApiError(error, t('projects.createFailed') || 'Failed to create project');
    }
  };

  const handleEdit = async () => {
    if (!editingProject || !workspaceId) return;

    // Validate form
    const isValid = await validateForm();
    if (!isValid) return;

    try {
      await updateProject({
        workspaceId,
        projectId: editingProject.id,
        data: {
      name: formData.name,
      description: formData.description,
      memberIds: formData.memberIds,
        },
      }).unwrap();

    setEditingProject(null);
    toast.success(t('projects.projectUpdated') || 'Project updated successfully');
    } catch (error) {
      console.error('Failed to update project:', error);
      handleApiError(error, t('projects.updateFailed') || 'Failed to update project');
    }
  };

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({
      name: "",
      description: "",
      memberIds: [],
    });
    setErrors({});
    setIsCreateModalOpen(true);
  };

  const openEditModal = (project: Project) => {
    setIsCreateModalOpen(false);
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || "",
      memberIds: project.memberIds,
    });
    setErrors({});
  };

  const handleDeleteClick = (projectId: string) => {
    setDeleteProjectId(projectId);
  };

  const handleConfirmDelete = async () => {
    if (!workspaceId || !deleteProjectId) return;
    setIsDeleting(true);
    try {
      await deleteProject({ workspaceId, projectId: deleteProjectId }).unwrap();
      toast.success(t('projects.projectDeleted') || 'Project deleted successfully');
      setDeleteProjectId(null);
    } catch (error) {
      console.error('Failed to delete project:', error);
      handleApiError(error, t('projects.deleteFailed') || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchiveClick = (projectId: string) => {
    setArchiveProjectId(projectId);
  };

  const handleConfirmArchive = async () => {
    if (!workspaceId || !archiveProjectId) return;
    setIsArchiving(true);
    try {
      await archiveProject({ workspaceId, projectId: archiveProjectId }).unwrap();
      toast.success(t('projects.projectArchived') || 'Project archived successfully');
      setArchiveProjectId(null);
    } catch (error) {
      console.error('Failed to archive project:', error);
      handleApiError(error, t('projects.archiveFailed') || 'Failed to archive project');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleUnarchiveClick = (projectId: string) => {
    setUnarchiveProjectId(projectId);
  };

  const handleConfirmUnarchive = async () => {
    if (!workspaceId || !unarchiveProjectId) return;
    setIsUnarchiving(true);
    try {
      await unarchiveProject({ workspaceId, projectId: unarchiveProjectId }).unwrap();
      toast.success(t('projects.projectUnarchived') || 'Project unarchived successfully');
      setUnarchiveProjectId(null);
    } catch (error) {
      console.error('Failed to unarchive project:', error);
      handleApiError(error, t('projects.unarchiveFailed') || 'Failed to unarchive project');
    } finally {
      setIsUnarchiving(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <div className="mb-8">
        <p className="text-gray-600">
          {t('projects.description')}
        </p>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <div className="flex items-center justify-between mb-6">
          <TabsList>
            <TabsTrigger value="active">
              {language === 'ja' ? 'アクティブ' : 'Active'} ({activeProjects.length})
            </TabsTrigger>
            <TabsTrigger value="archived">
              {language === 'ja' ? 'アーカイブ' : 'Archived'} ({archivedProjects.length})
            </TabsTrigger>
          </TabsList>
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            {t('projects.createProject')}
          </Button>
        </div>

        <TabsContent value="active" className="mt-6">
          {isLoadingProjects ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">{t('common.loading') || 'Loading...'}</p>
            </div>
          ) : activeProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">{t('projects.noProjects')}</p>
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                {language === 'ja' ? '最初のプロジェクトを作成' : 'Create First Project'}
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">{t('projects.projectName')}</TableHead>
                    <TableHead className="w-[300px]">{t('projects.projectDescription')}</TableHead>
                    <TableHead className="text-center">{language === 'ja' ? '繰り返しタスク' : 'Recurring Tasks'}</TableHead>
                    <TableHead className="text-center">{language === 'ja' ? '単発タスク' : 'One-time Tasks'}</TableHead>
                    <TableHead>{t('projects.projectMembers')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeProjects.map((project) => {
                    const members = users.filter((u) => project.memberIds.includes(u.id));

                    return (
                      <TableRow 
                        key={project.id} 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <TableCell className="w-[250px]">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                              <FolderKanban className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="text-gray-900 break-words whitespace-normal">{project.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[300px]">
                          <span className="text-gray-600 text-sm break-words whitespace-normal">
                            {project.description || '-'}
                          </span>
                        </TableCell>
                          <ProjectTaskCounts projectId={project.id} />
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {members.length === 0 ? (
                              <span className="text-gray-400 text-sm">-</span>
                            ) : (
                              members.slice(0, 3).map((user) => (
                                <div
                                  key={user.id}
                                  className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs"
                                  title={user.name}
                                >
                                  {user.name.charAt(0)}
                                </div>
                              ))
                            )}
                            {members.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs">
                                +{members.length - 3}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(project);
                              }}
                              title={t('common.edit')}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchiveClick(project.id);
                              }}
                              title={t('projects.archiveProject')}
                            >
                              <Archive className="w-4 h-4 text-gray-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(project.id);
                              }}
                              title={t('common.delete')}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="archived" className="mt-6">
          {archivedProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">{language === 'ja' ? 'アーカイブされたプロジェクトがありません' : 'No archived projects'}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">{t('projects.projectName')}</TableHead>
                    <TableHead className="w-[300px]">{t('projects.projectDescription')}</TableHead>
                    <TableHead className="text-center">{language === 'ja' ? '繰り返しタスク' : 'Recurring Tasks'}</TableHead>
                    <TableHead className="text-center">{language === 'ja' ? '単発タスク' : 'One-time Tasks'}</TableHead>
                    <TableHead>{t('projects.projectMembers')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archivedProjects.map((project) => {
                    const members = users.filter((u) => project.memberIds.includes(u.id));

                    return (
                      <TableRow 
                        key={project.id} 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <TableCell className="w-[250px]">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                              <FolderKanban className="w-4 h-4 text-gray-600" />
                            </div>
                            <span className="text-gray-900 break-words whitespace-normal">{project.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[300px]">
                          <span className="text-gray-600 text-sm break-words whitespace-normal">
                            {project.description || '-'}
                          </span>
                        </TableCell>
                          <ProjectTaskCounts projectId={project.id} />
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {members.length === 0 ? (
                              <span className="text-gray-400 text-sm">-</span>
                            ) : (
                              members.slice(0, 3).map((user) => (
                                <div
                                  key={user.id}
                                  className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs"
                                  title={user.name}
                                >
                                  {user.name.charAt(0)}
                                </div>
                              ))
                            )}
                            {members.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs">
                                +{members.length - 3}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnarchiveClick(project.id);
                              }}
                              title={t('projects.unarchiveProject')}
                            >
                              <ArchiveRestore className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(project.id);
                              }}
                              title={t('common.delete')}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* 新規作成・編集モーダル */}
      <Dialog
        open={isCreateModalOpen || editingProject !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateModalOpen(false);
            setEditingProject(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingProject ? t('projects.editProject') : t('projects.createProject')}
            </DialogTitle>
            <DialogDescription>
              {editingProject
                ? t('projects.editProjectDescription')
                : t('projects.createProjectDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="mb-2" htmlFor="project-name">{t('projects.projectNameRequired')}</Label>
              <Input
                id="project-name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  // Clear error when user types
                  if (errors.name) {
                    setErrors({ ...errors, name: '' });
                  }
                }}
                placeholder={t('projects.projectNameExample')}
                maxLength={50}
                className={errors.name ? "border-red-500" : ""}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <Label className="mb-2" htmlFor="project-description">{t('projects.projectDescriptionLabel')}</Label>
              <Textarea
                id="project-description"
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  // Clear error when user types
                  if (errors.description) {
                    setErrors({ ...errors, description: '' });
                  }
                }}
                placeholder={t('projects.projectDescriptionPlaceholder')}
                rows={3}
                maxLength={150}
                className={errors.description ? "border-red-500" : ""}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description}</p>
              )}
            </div>

            <div>
              <Label className="mb-3 block">{t('projects.projectMembersLabel')} *</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`member-${user.id}`}
                      checked={formData.memberIds.includes(user.id)}
                      onCheckedChange={(checked) => {
                        let newMemberIds = [...formData.memberIds];
                        if (checked) {
                          newMemberIds.push(user.id);
                        } else {
                          newMemberIds = newMemberIds.filter((id) => id !== user.id);
                        }
                        
                        setFormData({
                          ...formData,
                          memberIds: newMemberIds,
                        });
                        
                        // Clear error when user selects a member
                        if (errors.memberIds && newMemberIds.length > 0) {
                          setErrors({ ...errors, memberIds: "" });
                        }
                      }}
                    />
                    <label
                      htmlFor={`member-${user.id}`}
                      className="text-gray-700 cursor-pointer flex items-center gap-2"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs">
                        {user.name.charAt(0)}
                      </div>
                      {user.name}
                    </label>
                  </div>
                ))}
              </div>
              {errors.memberIds && (
                <p className="mt-1 text-sm text-red-500">{errors.memberIds}</p>
              )}
            </div>

            {!editingProject && (
              <div>
                <Label className="mb-3 block">{t('projects.templateSelect')}</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="template-select"
                      checked={showTemplateSelect}
                      onCheckedChange={(checked) => {
                        setShowTemplateSelect(!!checked);
                      }}
                    />
                    <label
                      htmlFor="template-select"
                      className="text-gray-700 cursor-pointer flex items-center gap-2"
                    >
                      {t('projects.useTemplate')}
                    </label>
                  </div>
                  {showTemplateSelect && (
                    <select
                      id="project-template"
                      value={selectedTemplateId}
                      onChange={(e) =>
                        setSelectedTemplateId(e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">{t('projects.selectTemplate')}</option>
                      {taskTemplates.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingProject(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={editingProject ? handleEdit : handleCreate}
              disabled={!formData.name || formData.memberIds.length === 0 || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.creating') || 'Creating...'}
                </>
              ) : editingProject ? (
                t('common.update')
              ) : (
                t('common.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={!!deleteProjectId} onOpenChange={(open) => !open && setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('projects.deleteProject')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteProjectId && (
                <>
                  <span className="block mb-2">
                    {t('projects.deleteWarning')}
                  </span>
                  <span className="block px-3 py-2 bg-gray-50 rounded border border-gray-200 text-gray-900">
                    {projects.find(p => p.id === deleteProjectId)?.name}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Modal */}
      <AlertDialog open={!!archiveProjectId} onOpenChange={(open) => !open && setArchiveProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('projects.archiveProject')}</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveProjectId && (
                <>
                  <span className="block mb-2">
                    {t('projects.archiveDescription')}
                  </span>
                  <span className="block px-3 py-2 bg-gray-50 rounded border border-gray-200 text-gray-900 mb-2">
                    {projects.find(p => p.id === archiveProjectId)?.name}
                  </span>
                  <span className="block text-sm text-gray-600">
                    {t('projects.archiveHint')}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmArchive}
              disabled={isArchiving}
            >
              {isArchiving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('projects.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unarchive Confirmation Modal */}
      <AlertDialog open={!!unarchiveProjectId} onOpenChange={(open) => !open && setUnarchiveProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('projects.unarchiveProject')}</AlertDialogTitle>
            <AlertDialogDescription>
              {unarchiveProjectId && (
                <>
                  <span className="block mb-2">
                    {t('projects.unarchiveConfirm')}
                  </span>
                  <span className="block px-3 py-2 bg-gray-50 rounded border border-gray-200 text-gray-900">
                    {projects.find(p => p.id === unarchiveProjectId)?.name}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnarchiving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmUnarchive}
              disabled={isUnarchiving}
            >
              {isUnarchiving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('projects.unarchive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
